import React, { useCallback, useEffect, useState } from 'react';

const API_BASE = '/scenario-os';

function getToken() {
  return localStorage.getItem('scenario_os_token');
}

// ── Diff modal ─────────────────────────────────────────────────────────────

function DiffModal({ sceneId, v1, v2, onClose }) {
  const [diffData, setDiffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sceneId || v1 == null || v2 == null) return;
    const token = getToken();
    setLoading(true);
    setError('');
    fetch(
      `${API_BASE}/coach/scenes/${encodeURIComponent(sceneId)}/compare?v1=${v1}&v2=${v2}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => setDiffData(data))
      .catch((e) => setError(e.message || 'Failed to load diff.'))
      .finally(() => setLoading(false));
  }, [sceneId, v1, v2]);

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    zIndex: 1100,
    background: 'rgba(0,0,0,0.65)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const modalStyle = {
    width: 560,
    maxWidth: '95vw',
    maxHeight: '80vh',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 10,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
  };

  const fields = diffData?.diff || diffData?.changes || [];

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', background: '#1e293b', borderBottom: '1px solid #334155',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#f8fafc' }}>
            Compare v{v1} → v{v2}
          </span>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 0 }}
            aria-label="Close diff"
          >
            &#215;
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          {loading && (
            <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: 24 }}>Loading diff…</div>
          )}
          {error && (
            <div style={{ color: '#f87171', fontSize: 13, padding: '8px 0' }}>{error}</div>
          )}
          {!loading && !error && fields.length === 0 && (
            <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: 24 }}>
              No differences found between these versions.
            </div>
          )}
          {!loading && !error && fields.length > 0 && fields.map((change, i) => (
            <div key={i} style={{
              marginBottom: 16, background: '#1e293b', borderRadius: 6,
              border: '1px solid #334155', overflow: 'hidden',
            }}>
              <div style={{
                padding: '6px 12px', background: '#1e3a5f',
                fontSize: 11, fontWeight: 700, color: '#60a5fa', letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}>
                {change.field || change.key || `Field ${i + 1}`}
              </div>
              <div style={{ display: 'flex' }}>
                <div style={{ flex: 1, padding: '10px 12px', borderRight: '1px solid #334155' }}>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>v{v1}</div>
                  <pre style={{ margin: 0, fontSize: 12, color: '#fca5a5', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' }}>
                    {typeof change.old_value === 'object'
                      ? JSON.stringify(change.old_value, null, 2)
                      : String(change.old_value ?? '—')}
                  </pre>
                </div>
                <div style={{ flex: 1, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>v{v2}</div>
                  <pre style={{ margin: 0, fontSize: 12, color: '#86efac', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' }}>
                    {typeof change.new_value === 'object'
                      ? JSON.stringify(change.new_value, null, 2)
                      : String(change.new_value ?? '—')}
                  </pre>
                </div>
              </div>
            </div>
          ))}
          {/* Fallback: show raw JSON if server returned something unexpected */}
          {!loading && !error && diffData && !Array.isArray(fields) && (
            <pre style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(diffData, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function formatDate(isoString) {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
}

export default function VersionHistoryPanel({ sceneId, onRestore, isOpen, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [restoringVersion, setRestoringVersion] = useState(null);
  // compareTarget: { v1, v2 } | null — drives the diff modal
  const [compareTarget, setCompareTarget] = useState(null);

  const fetchVersions = useCallback(() => {
    if (!sceneId) return;
    const token = getToken();
    setLoading(true);
    setError('');
    fetch(`${API_BASE}/coach/scenes/${encodeURIComponent(sceneId)}/versions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setVersions(data.versions || []);
      })
      .catch((e) => {
        setError(e.message || 'Failed to load version history.');
      })
      .finally(() => setLoading(false));
  }, [sceneId]);

  useEffect(() => {
    if (isOpen) fetchVersions();
  }, [isOpen, fetchVersions]);

  const handleRestore = useCallback(
    async (versionNumber) => {
      if (!sceneId) return;
      const token = getToken();
      setRestoringVersion(versionNumber);
      try {
        const res = await fetch(
          `${API_BASE}/coach/scenes/${encodeURIComponent(sceneId)}/versions/${versionNumber}/restore`,
          { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        if (onRestore) onRestore(versionNumber);
      } catch (e) {
        setError(e.message || 'Restore failed.');
      } finally {
        setRestoringVersion(null);
      }
    },
    [sceneId, onRestore],
  );

  if (!isOpen) return null;

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
  };

  const panelStyle = {
    width: 380,
    maxWidth: '90vw',
    height: '100vh',
    background: '#0f172a',
    borderLeft: '1px solid #1e293b',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    background: '#1e293b',
    borderBottom: '1px solid #334155',
    flexShrink: 0,
  };

  const titleStyle = {
    fontSize: 14,
    fontWeight: 700,
    color: '#f8fafc',
  };

  const closeBtn = {
    background: 'transparent',
    border: 'none',
    color: '#94a3b8',
    fontSize: 20,
    cursor: 'pointer',
    lineHeight: 1,
    padding: 0,
  };

  const bodyStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 0',
  };

  const entryStyle = {
    padding: '12px 18px',
    borderBottom: '1px solid #1e293b',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  };

  const metaStyle = { flex: 1, minWidth: 0 };

  const versionBadge = {
    fontSize: 11,
    fontWeight: 700,
    color: '#60a5fa',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 2,
  };

  const reasonStyle = {
    fontSize: 13,
    color: '#e2e8f0',
    marginBottom: 2,
    wordBreak: 'break-word',
  };

  const metaRowStyle = {
    fontSize: 11,
    color: '#64748b',
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  };

  const restoreBtnStyle = (vNum) => ({
    background: restoringVersion === vNum ? '#1e3a5f' : '#1d4ed8',
    color: '#eff6ff',
    border: 'none',
    borderRadius: 4,
    padding: '4px 10px',
    fontSize: 11,
    fontWeight: 600,
    cursor: restoringVersion === vNum ? 'wait' : 'pointer',
    flexShrink: 0,
    opacity: restoringVersion !== null ? 0.7 : 1,
  });

  const compareBtnStyle = {
    background: 'transparent',
    color: '#60a5fa',
    border: '1px solid #334155',
    borderRadius: 4,
    padding: '4px 8px',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    flexShrink: 0,
  };

  return (
    <>
    {compareTarget && (
      <DiffModal
        sceneId={sceneId}
        v1={compareTarget.v1}
        v2={compareTarget.v2}
        onClose={() => setCompareTarget(null)}
      />
    )}
    <div style={overlayStyle} onClick={onClose} role="dialog" aria-modal="true" aria-label="Version History">
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <span style={titleStyle}>Version History</span>
          <button style={closeBtn} onClick={onClose} aria-label="Close version history">&#215;</button>
        </div>

        <div style={bodyStyle}>
          {loading && (
            <div style={{ padding: '24px', color: '#64748b', fontSize: 13, textAlign: 'center' }}>
              Loading…
            </div>
          )}
          {error && (
            <div style={{ padding: '16px 18px', color: '#f87171', fontSize: 13 }}>
              {error}
            </div>
          )}
          {!loading && !error && versions.length === 0 && (
            <div style={{ padding: '24px', color: '#64748b', fontSize: 13, textAlign: 'center' }}>
              No version history yet.
            </div>
          )}
          {!loading && versions.map((v, idx) => {
            const vNum = v.version || v.version_number;
            const reason = v.snapshot_reason || v.source || 'manual';
            const source = v.source || v.updated_by || '';
            const ts = v.snapshot_at || v.changed_at || v.updated_at;
            // Compare against the next newer version (idx - 1) or current (use vNum + 1)
            const newerVersion = idx > 0
              ? (versions[idx - 1].version || versions[idx - 1].version_number)
              : null;
            return (
              <div key={vNum} style={entryStyle}>
                <div style={metaStyle}>
                  <div style={versionBadge}>v{vNum}</div>
                  <div style={reasonStyle}>{reason}</div>
                  <div style={metaRowStyle}>
                    {source && <span>{source}</span>}
                    {ts && <span>{formatDate(ts)}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {newerVersion != null && (
                    <button
                      style={compareBtnStyle}
                      onClick={() => setCompareTarget({ v1: vNum, v2: newerVersion })}
                      aria-label={`Compare version ${vNum} with version ${newerVersion}`}
                    >
                      Compare
                    </button>
                  )}
                  <button
                    style={restoreBtnStyle(vNum)}
                    onClick={() => handleRestore(vNum)}
                    disabled={restoringVersion !== null}
                    aria-label={`Restore version ${vNum}`}
                  >
                    {restoringVersion === vNum ? 'Restoring…' : 'Restore'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
    </>
  );
}
